/**
 * Did the server reject this session, or did it never answer?
 *
 * `AuthContext.checkAuth` used to treat every failure of `GET /auth/me` the
 * same way — one `catch` calling `logout()`, and `logout()` calls
 * `purgeTenantData`, which empties IndexedDB including `syncQueue`. So a hand
 * whose phone reloaded the app in a dead zone was signed out AND had their
 * queued tickets deleted, by the same line, with nothing on screen to say so.
 * Measured: a reload with the API unreachable landed on /login with the pending
 * mutation gone.
 *
 * The two cases are not alike and only one of them is evidence about the token:
 *
 *   401 / 403  the server read the token and refused it. The session is over;
 *              purging is correct and the cached data belongs to nobody.
 *   anything   no answer, or an answer that says nothing about the token — a
 *   else       dropped connection, a timeout, DNS, CORS, a 502 from the edge, a
 *              500 from the API, a 200 whose body is not a profile. None of
 *              these mean the token is bad, and acting as if they did destroys
 *              work to enforce a rule nobody has actually broken.
 *
 * The asymmetry is deliberate: 'rejected' is the only verdict that deletes
 * anything, so it is the only one that requires the server to have said so. A
 * 500 is the API having a bad day, not a hand being signed out — the old code
 * purged on it.
 */
export type AuthProbeOutcome =
  /** The server answered and refused this token. */
  | 'rejected'
  /** Nothing usable came back. The token's validity is unknown, not disproved. */
  | 'unconfirmed';

/**
 * Thrown when `/auth/me` answers 200 with a body that is not a profile.
 *
 * A success status is not a confirmation — `checkAuth` reads
 * `response.data.user` directly, so an SPA-fallback `index.html`, an empty
 * body, or an envelope-shaped `{success, data}` all arrive as a 200 with no
 * `user` on it. Setting `undefined` there signs the hand out just as
 * thoroughly as a 401 and looks like a bug in the screen they land on. Raised
 * so it lands in the same place as a network failure, which is what it is:
 * we did not learn whether the token is good.
 */
export class MalformedProfileError extends Error {
  constructor() {
    super('GET /auth/me answered without a user');
    this.name = 'MalformedProfileError';
  }
}

/** The status a failed request came back with, if it came back at all. */
function statusOf(error: any): number | null {
  const status = error?.response?.status ?? error?.status;
  return typeof status === 'number' ? status : null;
}

export function classifyAuthFailure(error: any): AuthProbeOutcome {
  return statusOf(error) === 401 || statusOf(error) === 403
    ? 'rejected'
    : 'unconfirmed';
}
